import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {
  useDeleteServiceOrderVariantMutation,
  usePostServiceOrderVariantSetAsActiveMutation,
  usePostServiceOrderVariantSetAsDefaultMutation,
  usePostServiceOrderVariantSetAsInactiveMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function ServiceOrderVariants() {
  const navigate = useNavigate();

  const [postServiceOrderVariantSetAsActive] = usePostServiceOrderVariantSetAsActiveMutation();
  const [postServiceOrderVariantSetAsInactive] = usePostServiceOrderVariantSetAsInactiveMutation();
  const [deleteServiceOrderVariant] = useDeleteServiceOrderVariantMutation();
  const [postServiceOrderVariantSetAsDefault] = usePostServiceOrderVariantSetAsDefaultMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('redirectDetail', () =>
        navigate(composePath(settingsRoutes.serviceOrderVariantsDetail, {params: {id}}))
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteServiceOrderVariant({serviceOrderVariantId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('setAsActive', () =>
        postServiceOrderVariantSetAsActive({serviceOrderVariantId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('setAsInactive', () =>
        postServiceOrderVariantSetAsInactive({serviceOrderVariantId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('setAsDefault', () =>
        postServiceOrderVariantSetAsDefault({serviceOrderVariantId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .otherwise(showNotification.error);
  };

  return (
    <>
      <Helmet title={i18n.t('entity.order.labels.serviceOrderVariants')} />
      <SettingsTemplate
        header={{
          title: i18n.t('entity.order.labels.serviceOrderVariants'),
          actions: [
            {
              type: 'button',
              title: i18n.t('entity.order.actions.newServiceOrderVariant'),
              onClick: () => navigate(settingsRoutes.serviceOrderVariantsCreate),
            },
          ],
        }}
        data-testid={testIds.settings.serviceOrderVariants('template')}
      >
        <DmsDataGrid
          gridCode="service-order-variant"
          actionCallback={actionCallback}
          data-testid={testIds.settings.serviceOrderVariants('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
